import Axios from 'axios';
import store from '@/store';
import get from 'lodash/get';
import ApiService from '../../../services/apiService';

const httpClient = Axios.create({
  baseURL: `${process.env.VUE_APP_API_HOST}${process.env.VUE_APP_API_PATH}`,
  timeout: 5000,
  withCredentials: true,
  headers: {
    // requires Cache-Control for IE11 GET requests, otherwise they are not fetched again
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});
httpClient.defaults.cache = false;

let setAnnouncements = (tenantId, data) => {
  const now = new Date();
  store.state.app.announcements = data
    .map((announcement) => {
      // if expired set state to inactive
      const endDate = new Date(announcement.end);
      if (announcement.end && endDate < now && tenantId) {
        ApiService.tenantUpdateSystemAnnouncementState(tenantId, announcement);
        return null;
      }
      if (announcement && announcement.active) {
        return {
          ...announcement,
        };
      }
      return null;
    })
    .filter((a) => !!a);
};

export default {
  login({ commit }, userData) {
    this.state.app.permissions = null;

    return new Promise((resolve, reject) => {
      commit('auth_request');
      httpClient
        .post('/auth/login', userData)
        .then((resp) => {
          const token = resp.data.token;
          httpClient.defaults.headers.common['Authorization'] = token;
          commit('auth_success', { token, data: { ...resp.data, originalToken: undefined } });
          resolve(resp);
        })
        .catch((error) => {
          commit('logout');
          localStorage.removeItem('token');
          delete httpClient.defaults.headers.common['Authorization'];
          if (error.response) {
            reject(error.response);
          } else {
            reject(error);
          }
        });
    });
  },
  loginWithToken({ commit }, token) {
    this.state.app.permissions = null;

    const verifyOpts = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        domain: window.location.hostname,
      },
    };

    return httpClient
      .get('/auth/verify', verifyOpts)
      .then(({ data }) => {
        if (!data.isAuthenticated) {
          throw new Error('failed isAuthenticated');
        }
        const token = data.token;
        httpClient.defaults.headers.common['Authorization'] = token;

        this.state.app.featureFlags = data.featureFlags.flags;

        commit('auth_success', { token, data });

        return data;
      })
      .catch((error) => {
        commit('logout');
        delete httpClient.defaults.headers.common['Authorization'];
        throw error;
      });
  },
  verify({ commit }, apiKey = null) {
    let token = localStorage.getItem('token');
    if (!token) {
      token = '000000000000000000000'; // ToDo: Andere Lösung finden, Workaround da sonst die Login Seite nicht aufgefrufen wird.
    }

    // PATH:
    // /api/v1 auth.verify
    // koa-middleware `jwt.js`
    // jwt.verify
    // jwt.resolveToken
    // users.findById
    if (!apiKey) {
      httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      delete httpClient.defaults.headers.common['X-API-Key'];
      this.state.app.isApiKey = false;
    } else {
      delete httpClient.defaults.headers.common['Authorization'];
      httpClient.defaults.headers.common['X-API-Key'] = apiKey;
      this.state.app.isApiKey = true;
    }
    return httpClient
      .get('/auth/verify', { params: { domain: window.location.hostname } })
      .then(({ data }) => {
        if (!data.user) {
          throw new Error('Auth error');
        }
        if (!data.isAuthenticated) {
          throw new Error('failed isAuthenticated');
        }

        if (!apiKey) {
          httpClient.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }

        this.state.app.featureFlags = data.featureFlags.flags;

        let tenantId = data.tenantId;
        return httpClient
          .get('/user/own/announcements')
          .then((announcements) => {
            if (announcements && announcements.data.length) {
              setAnnouncements(tenantId, announcements.data);
            }

            return data.user;
          })
          .catch((err) => {
            // ignore errors here
            console.error('Error getting announcements', err);
          })
          .finally(() => {
            commit('auth_success', {
              token: data.token,
              data: { ...data, isApiKey: !!apiKey },
            });
          });
      })
      .catch((error) => {
        if (error.code === 'ECONNABORTED' || get(error, 'response.status') === 502) {
          // only if the token is last token-verification is older than half an hour, logout the user
          // check for the user-id to make sure the user is currently logged in
          if (
            get(this.state, 'authentication.user.id') &&
            new Date() - new Date(localStorage.getItem('tokenTimestamp')) < 1800 * 1000
          ) {
            return;
          }
        }
        delete httpClient.defaults.headers.common['Authorization'];
        commit('logout');
        throw error;
      });
  },
  impersonate({ commit }, userId) {
    if (this.state.authentication && this.state.authentication.originalToken) {
      return;
    }

    let token = localStorage.getItem('token');
    if (!token) {
      token = '000000000000000000000'; // ToDo: Andere Lösung finden, Workaround da sonst die Login Seite nicht aufgefrufen wird.
    }

    delete this.state.app.permissions;

    // PATH:
    // /api/v1 auth.impersonate
    // koa-passport LocalStrategy "impersonate"
    // auth.impersonate
    // users.findByEmail
    // jwt.sign
    httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return httpClient.post('/auth/impersonate', { userId }).then(({ data }) => {
      if (!data.user || !data.originalToken) {
        throw new Error('Impersonate error');
      }
      commit('auth_success', {
        token: data.token,
        data,
      });
    });
  },
  restoreOriginal() {
    localStorage.setItem('token', localStorage.getItem('originalToken'));
    localStorage.removeItem('originalToken');
  },
  logout({ commit }) {
    localStorage.removeItem('originalToken');
    this.state.app.permissions = null;
    this.state.app.dtwins = {};
    this.state.authentication = { access: [], user: { id: null } };
    this.state.app.announcements = [];

    return httpClient.get('/auth/logout').finally(() => {
      delete httpClient.defaults.headers.common['Authorization'];
      commit('logout');
    });
  },
};
